/* Copyright (c) 2020-present CloudIO Inc */

import { isNotEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import { ItemType } from '../../registry/Base.type';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionLoopRecords';

export const loopTypes = {
  all: 'All',
  selected: 'Selected',
  dirty: 'Dirty',
} as const;

const ActionLoopRecordsManager: ActionManagerFn<'ActionLoopRecords'> = (
  platform: Platform,
): ActionManager<'ActionLoopRecords'> => {
  return {
    ...baseActionManager(platform, 'ActionLoopRecords'),
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ActionAbort',
          'ActionInvokeCloudFunction',
          'ActionInvokeControllerFunction',
          'ActionLoopDeselect',
          'ActionLoopSelect',
          'ActionSet',
          'ActionSetAttributeError',
          'ActionShowMessage',
          'ActionSleep',
          'ActionSwitch',
        ] as ItemType[]
      ).includes(childType as ItemType),
    defaultValue,
    defaultChildren: ['ActionSet'],
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'DataStore',
          'LocalDataStore',
        ],
      },
      loop: {
        label: 'Loop',
        type: 'radio',
        options: loopTypes,
      },
      matchCondition: {
        label: 'Match Condition',
        type: 'expression',
        returnType: async () => 'boolean',
        helpText: `Specify an expression that should be evaluated to a boolean [e.g. #{dsAlias.attr1} == 'X' && #{dsAlias.attr2} > 1]. This expression
        will be evaluated for each record in the loop and only the matching
        records will be used to execute the child actions. If no expression
        is specified, it will be considered as true`,
      },
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'STORE'
        ? { aliasProp: 'alias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    name: 'Loop & Update Records',
    getDisplayText: (item) => {
      let label = `${item.props.alias} - ${item.props.loop}`;
      if (isNotEmpty(item.props.matchCondition.value)) {
        label = `${label} (${item.props.matchCondition.value})`;
      }
      return label;
    },
    description:
      'Use this action to update one or more records in the Data Store. You choose whether to loop over all, selected, or dirty (updated) ones. The records can be further filtered using a match condition.',
  };
};
export default ActionLoopRecordsManager;
